import { green, grey } from '@mui/material/colors';
import { OlErrorKeys, OneLinkStatusEnum } from '../types/oneLink';
import { EMAIL_ADDRESS, FIRST_NAME, LAST_NAME, PHONE_NUMBER } from './oneLinkKeys';

export { EMAIL_ADDRESS, FIRST_NAME, LAST_NAME, PHONE_NUMBER, TITLE, UID, URL } from './oneLinkKeys';

export const MAX_ONE_LINKS_PER_PAGE = 50;
export const MAX_RECIPIENTS_PER_PAGE = 10;
export const MAX_REDEMPTION_LIMIT = 9999999;

export const ONE_LINK_STATUS = {
  [OneLinkStatusEnum.Active]: {
    label: 'Active',
    color: green[500],
  },
  [OneLinkStatusEnum.NotActive]: {
    label: 'Inactive',
    color: grey[400],
  },
};

export const ERROR_EMAIL_OR_PHONE_REQUIRED = 'Email or phone is required';
export const ERROR_DUPLICATE_EMAIL = 'Duplicate email';
export const ERROR_DUPLICATE_PHONE = 'Duplicate phone';
export const ERROR_EMAIL_INVALID = 'Email is invalid';
export const ERROR_PHONE_INVALID = 'Phone is invalid';

export const ALLOWED_RECIPIENTS_TABLE_HEADERS = {
  [FIRST_NAME]: 'First name',
  [LAST_NAME]: 'Last name',
  [EMAIL_ADDRESS]: 'Email address',
  [PHONE_NUMBER]: 'Phone number',
};

export const ONE_LINK_REDEMPTION_LIMIT = 10;

export enum RowsPerPageEnum {
  Ten = 10,
  Twenty = 20,
  TwentyFive = 25,
  Fifty = 50,
  OneHundred = 100,
}

export const DEFAULT_PAGE_NUMBER = 1;
export const DISABLED_SUBMIT_BUTTON_HINT =
  'No changes have been made to the form. Please make a change or click Cancel.';

export const COLUMN_IDS = {
  TITLE: 'title',
  CAMPAIGN_NAME: 'campaign_name',
  ONE_LINK_URL: 'one_link_url',
  CREATED_BY: 'created_by',
  SENDER: 'sender',
  CONFIRMED_SENDS: 'confirmed_sends',
  REDEMPTION_LIMIT: 'redemption_limit',
  SETTINGS: 'settings',
  CREATED_AT: 'created_at',
  EXPIRATION_DATE: 'expiration_date',
  ACTIONS: 'actions',

  BUSINESS_REASON: 'business_reason',
  INTERNAL_NOTES: 'internal_notes',
  STATUS: 'status',
  DISABLED_BY: 'disabled_by',
  ON_HOLD_UNTIL: 'on_hold_until',
  DEPARTMENT: 'department',
  ITEM: 'item',
  ITEM_SOURCE: 'item_source',
  GIFT_BUDGET: 'gift_budget',
};

export const VISIBLE_ONE_LINKS_COLS_INITIAL_STATE = {
  [COLUMN_IDS.TITLE]: true,
  [COLUMN_IDS.CAMPAIGN_NAME]: true,
  [COLUMN_IDS.ONE_LINK_URL]: true,
  [COLUMN_IDS.CREATED_BY]: true,
  [COLUMN_IDS.SENDER]: true,
  [COLUMN_IDS.CONFIRMED_SENDS]: true,
  [COLUMN_IDS.REDEMPTION_LIMIT]: true,
  [COLUMN_IDS.SETTINGS]: true,
  [COLUMN_IDS.CREATED_AT]: true,
  [COLUMN_IDS.EXPIRATION_DATE]: true,
  [COLUMN_IDS.ACTIONS]: true,
  [COLUMN_IDS.BUSINESS_REASON]: false,
  [COLUMN_IDS.INTERNAL_NOTES]: false,
  [COLUMN_IDS.STATUS]: true,
  [COLUMN_IDS.DISABLED_BY]: false,
  [COLUMN_IDS.ON_HOLD_UNTIL]: false,
  [COLUMN_IDS.DEPARTMENT]: true,
  [COLUMN_IDS.ITEM]: true,
  [COLUMN_IDS.ITEM_SOURCE]: true,
  [COLUMN_IDS.GIFT_BUDGET]: true,
};

export const ERROR_MESSAGES: Record<string, string> = {
  // frontend errors
  [OlErrorKeys.ExpirationDateOutdated]: 'Expiration date must be in the future.',
  [OlErrorKeys.SenderDeleted]: 'This sender is no longer available. Please select another.',
  [OlErrorKeys.OnHoldDateOutdated]: 'The "On Hold Until" date must be in the future.',
  [OlErrorKeys.BusinessReasonRequired]: 'Please provide a business reason.',
  [OlErrorKeys.RedemptionLimitLessThanAvailableSends]: 'Redemption limit must exceed confirmed sends.',
  [OlErrorKeys.OutOfStockItems]: 'Some items are out of stock. Please update your selection.',
  [OlErrorKeys.LessThanTwoValidCampaigns]: 'Select at least two valid campaigns.',
  [OlErrorKeys.InvalidAddresses]: 'Some addresses are invalid or inactive. Please review your list.',
  [OlErrorKeys.InvalidAddress]: 'Please check the address in your send.',
  [OlErrorKeys.ExperienceNotActive]: 'The selected template is inactive. Please choose an active one.',
  // backend errors
  [OlErrorKeys.SomeItemsInvalid]: 'One or more items are invalid. Please review your selection.',
  [OlErrorKeys.SomeCampaignsNotFound]: 'Some campaigns couldn’t be found. Please verify your list.',
  [OlErrorKeys.SomeCampaignsNotActive]: 'Some campaigns are inactive. Please choose active ones.',
  [OlErrorKeys.PickYourGiftNeedsTwoOrMoreCampaigns]: 'Pick your gift requires at least two campaigns.',
  [OlErrorKeys.EngagementHasBothItemsAndPickYourGiftCampaigns]: 'You can’t combine items and Pick your gift campaigns.',
  [OlErrorKeys.CampaignNotFound]: 'Campaign not found. Please check the campaign name or ID.',
  [OlErrorKeys.CampaignNotActive]: 'The selected campaign is inactive.',
  [OlErrorKeys.BusinessReasonInvalid]: 'Invalid business reason. Please enter a valid one.',
  [OlErrorKeys.MaxSubmitCountInvalidValue]: 'Invalid maximum submit count: %v.',
  [OlErrorKeys.MaxSubmitCountLessThanOneLinkUsed]: 'Submit count must be at least %v (current OneLink uses).',
  [OlErrorKeys.SenderNotFound]: 'Sender not found. Please select a valid sender.',
  [OlErrorKeys.SenderNotFromSameOrganization]: 'Sender must belong to your organization.',
  [OlErrorKeys.ExpirationDateShouldBeInFuture]: 'Expiration date must be in the future.',
  [OlErrorKeys.ReceiverAddressDuplicated]: 'Duplicate receiver address found. Please remove duplicates.',
  [OlErrorKeys.ReceiverAddressMissing]: 'Receiver address is required.',
  [OlErrorKeys.ErrorHappenedWhileFindingAddresses]: 'Error finding addresses. Please try again.',
  [OlErrorKeys.SomeAddressesDontBelongToCurrentOrganization]: 'Some addresses don’t belong to your organization.',
  [OlErrorKeys.OnlyActiveAddressesCanBeAddedToAddressBook]: 'Only active addresses can be added to the address book.',
  [OlErrorKeys.ExperienceNotFound]: 'The selected template was not found or is no longer accessible.',
  [OlErrorKeys.ExperienceInvalid]: 'The selected template is invalid. Please choose a different one.',
  [OlErrorKeys.ExperienceNotFromSameOrganization]: 'The selected template does not belong to your organization.',
};

export const EXPIRATION_DATE_ERROR_TOOLTIP_TEXT = 'Expiration date cannot be in the past. Choose a valid date.';
export const ON_HOLD_ERROR_TOOLTIP_TEXT =
  'The On Hold Date has already passed. Set a new date in the present or future.';
export const SHIPPING_ERROR_TOOLTIP_TEXT =
  'Some of the selected addresses are no longer valid. To activate the One Link, please update your selection by choosing a new address or removing the invalid one.';
export const SENDER_ERROR_TOOLTIP_TEXT = 'The selected sender is inactive. Select a different sender to proceed.';
export const REDEMPTION_LIMIT_ERROR_TOOLTIP_TEXT =
  'The redemption limit must be greater than or equal to the number of available sends.';
export const INVENTORY_ERROR_TOOLTIP_TEXT =
  'Some of the sent items are out of stock. To activate the One Link, please update your selection by choosing available items or removing the unavailable ones.';
export const BUSINESS_REASON_ERROR_TOOLTIP_TEXT = 'Please enter a valid business reason.';
export const TEMPLATE_ERROR_TOOLTIP_TEXT =
  'The selected template is not active. Please choose an active template to proceed.';

export const GIFT_BUDGET_TOOLTIP_TEXT =
  'The full budget is charged when a recipient picks from the marketplace; anything under budget is refunded';

export const ONE_LINK_SUMMARY_LABELS = {
  redemptions: 'Redemptions',
  expirationDate: 'Expiration date',
  itemSource: 'Item source',
  giftBudget: 'Gift budget',
  createdBy: 'Created by',
  sender: 'Sender',
  copyLink: 'Copy link',
};

export const NO_EXPIRATION_DATE_LABEL = 'No expiration date';

export const CUSTOM_SCENARIOS_SECTION_TITLE = 'Custom scenarios';
export const CUSTOM_SCENARIOS_HINT = 'Configured by the development team. Read-only.';
